/**
 * RoleWizardStepCriteria.jsx — Phase 0.6 Slice B Step 3.
 *
 * Writes the criteria mirror via PATCH /employer/roles/{id}/criteria.
 * Per pre-code plan §5.5, this step does NOT back-propagate into
 * `structured_definition_json` — the mirror is the canonical truth
 * for evaluation-time fields:
 *   - required_criteria / preferred_criteria (chips, mirrored from
 *     must/nice skills at publish; freely editable here)
 *   - dealbreakers (four locked keys)
 *   - dimension_weights (5 sliders; sum must = 100 when all present)
 *   - evaluation_metadata.notes (free text)
 *
 * The whole step is gated on the role having been created already
 * (criteria PATCH cannot precede the POST /roles draft creation).
 * The parent wizard renders a sentinel notice when `roleId` is null.
 */
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import CriteriaChipInput from './CriteriaChipInput';
import DealbreakersEditor from './DealbreakersEditor';
import DimensionWeightSliders from './DimensionWeightSliders';

const MAX_NOTES_LEN = 1000;

export default function RoleWizardStepCriteria({
  roleId,
  criteria,
  onCriteriaChange,
}) {
  if (!roleId) {
    return (
      <div
        className="p-6 text-sm text-slate-300"
        data-testid="wizard-step-criteria-locked"
      >
        Save the draft on Step 1 (a title is enough) before refining
        criteria — these settings live in a companion table that needs
        the role row to exist first.
      </div>
    );
  }

  const c = criteria || {};
  const patch = (p) => onCriteriaChange({ ...c, ...p });
  const notes = (c.evaluation_metadata && c.evaluation_metadata.notes) || '';

  return (
    <div className="space-y-6 p-6" data-testid="wizard-step-criteria">
      <header>
        <h2 className="text-base font-semibold text-ndor-ink">
          Criteria &amp; dealbreakers
        </h2>
        <p className="text-sm text-slate-300">
          Criteria refinements take effect at evaluation time. They do
          not change publish requirements — the publish gate only reads
          the role definition from Steps 1 &amp; 2.
        </p>
      </header>

      <section className="space-y-2">
        <Label className="text-sm">Required criteria</Label>
        <CriteriaChipInput
          value={c.required_criteria || []}
          onChange={(v) => patch({ required_criteria: v })}
          placeholder="Add a required criterion and press Enter…"
          tone="emerald"
          dataTestid="required-criteria"
        />
      </section>

      <section className="space-y-2">
        <Label className="text-sm">Preferred criteria</Label>
        <CriteriaChipInput
          value={c.preferred_criteria || []}
          onChange={(v) => patch({ preferred_criteria: v })}
          placeholder="Add a preferred criterion and press Enter…"
          tone="sky"
          dataTestid="preferred-criteria"
        />
      </section>

      <section className="space-y-2">
        <Label className="text-sm">Dealbreakers</Label>
        <DealbreakersEditor
          value={c.dealbreakers || {}}
          onChange={(v) => patch({ dealbreakers: v })}
        />
      </section>

      <section className="space-y-2">
        <Label className="text-sm">Dimension weights</Label>
        <DimensionWeightSliders
          value={c.dimension_weights || {}}
          onChange={(v) => patch({ dimension_weights: v })}
        />
      </section>

      <section className="space-y-2">
        <Label htmlFor="criteria-notes" className="text-sm">
          Evaluation notes
        </Label>
        <Textarea
          id="criteria-notes"
          value={notes}
          onChange={(e) => {
            const next = e.target.value.slice(0, MAX_NOTES_LEN);
            const meta = { ...(c.evaluation_metadata || {}) };
            if (next) meta.notes = next;
            else delete meta.notes;
            patch({ evaluation_metadata: meta });
          }}
          rows={4}
          maxLength={MAX_NOTES_LEN + 1}
          placeholder="Free-text notes for reviewers — kept on the role; never shown to candidates."
          data-testid="criteria-notes-input"
        />
        <p className="text-xs text-slate-300">
          {notes.length} / {MAX_NOTES_LEN} chars
        </p>
      </section>
    </div>
  );
}
